import { createContext } from 'preact';
import { useContext, useEffect, useRef } from 'preact/hooks';
import type { ComponentChildren } from 'preact';
import { t } from '../../i18n/index.js';
import { formatDuration } from '../../arena/useArena.js';
import { playSound } from '../../audio/sounds.js';
import { useTurnTicks } from './useTurnTicks.js';
import { turnClock } from '../../../shared/arena/turnClock.js';
import type { GameStateView } from '../../../shared/protocol.js';

/**
 * Set when a finished game's final position is shown (user request 2026-09-29): the turn banner
 * shows the result instead of whose turn it is. The state then has no turn.
 */
export const GameOverContext = createContext<{ text: string; outcome: 'win' | 'loss' | 'none' } | null>(null);

export function GameOverBanner(props: { over: { text: string; outcome: string } }) {
  return (
    <div class={`turn-banner over over-${props.over.outcome}`} role="status">
      <div class="turn-text">{props.over.text}</div>
    </div>
  );
}

/**
 * Common turn indicator for turn-based games: whose turn, the turn timer as a bar (no numbers)
 * and the time bank as a countdown once it runs (suunnitelma.md sections 18–19). Plays the
 * turn and time bank warning sounds.
 */
export function TurnStatus(props: { state: GameStateView; serverNow: number; children?: ComponentChildren }) {
  const { state, serverNow } = props;
  const myTurn = state.turn?.userId === state.me.id;
  const current = myTurn ? state.me : state.opponent;
  const clock = state.turn
    ? turnClock({
        turnStartedAt: state.turn.startedAt,
        turnTimeMs: state.settings.turnTimeMs,
        timeBankMs: current.timeBankMs,
        now: serverNow,
      })
    : null;
  const fraction = clock ? Math.max(0, Math.min(1, clock.turnRemainingMs / state.settings.turnTimeMs)) : 0;

  const first = useRef(true);
  useEffect(() => {
    if (!first.current && myTurn) playSound('turn', 300);
    first.current = false;
  }, [state.turn?.userId]);

  useTurnTicks(clock, myTurn);

  const over = useContext(GameOverContext);
  if (over) return <GameOverBanner over={over} />;
  return (
    <div class={`turn-banner ${myTurn ? 'mine' : 'theirs'}`} role="status" key={state.turn?.startedAt}>
      <div class="turn-text">
        {myTurn ? t.turn.yours : t.turn.theirs(state.opponent.displayName)}
        {props.children}
      </div>
      {clock && clock.phase === 'turn' && (
        <div class="turn-bar" aria-hidden="true">
          <div class="turn-bar-fill" style={{ width: `${fraction * 100}%` }} />
        </div>
      )}
      {clock && clock.phase !== 'turn' && (
        <div class="time-bank">
          {myTurn ? t.turn.timeBank : t.turn.opponentTimeBank}: <strong>{formatDuration(clock.bankRemainingMs)}</strong>
        </div>
      )}
    </div>
  );
}
